import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { PlotCard } from "../PlotCard";

interface Props {
  fromIso: string;
  toIso: string;
}

/**
 * Cumulative bentonite delivered + theoretical overcut volume + ratio
 * (delivered / overcut) vs chainage. Mirrors `Overcut_Graph.png`.
 *
 * Overcut volume is computed deterministically from the drive's pipe
 * OD + overcut (mm) settings. If either is missing, the overcut/ratio
 * traces are hidden and only the cumulative-delivered trace shows.
 */
export function OvercutChart({ fromIso, toIso }: Props) {
  const drive = useQuery({ queryKey: ["drive"], queryFn: api.drive });
  const series = useQuery({
    queryKey: ["lubrication", "series", fromIso, toIso],
    queryFn: () => api.lubrication.series({ from: fromIso, to: toIso }),
    refetchInterval: 60_000,
  });

  const pipeOdMm = drive.data?.pipeOdMm ?? null;
  const overcutMm = drive.data?.pipeSchedule?.overcutMm ?? null;

  const data = useMemo(() => {
    const records = (series.data?.records ?? []).filter(
      (r) => r.chainageM != null && r.actVolM3 != null,
    );

    const x = records.map((r) => r.chainageM);
    const cumL = records.map((r) => (r.actVolM3 ?? 0) * 1000);

    const traces: Record<string, unknown>[] = [
      {
        x, y: cumL,
        type: "scatter", mode: "lines",
        name: "Total volume (L)",
        line: { color: "#000", width: 1.4 },
      },
    ];

    if (pipeOdMm && overcutMm) {
      // Annular volume per metre: π × OD × overcut → m². × 1m × 1000 → L
      const odM = pipeOdMm / 1000;
      const overcutM = overcutMm / 1000;
      const overcutLPerM = Math.PI * odM * overcutM * 1000;
      const overcutL = (records as { chainageM: number | null }[]).map((r) =>
        r.chainageM != null ? r.chainageM * overcutLPerM : null,
      );
      const ratio = cumL.map((v, i) => {
        const ov = overcutL[i];
        return ov && ov > 0 ? v / ov : null;
      });
      traces.push(
        {
          x, y: overcutL,
          type: "scatter", mode: "lines",
          name: "Overcut volume (L)",
          line: { color: "#7e22ce", width: 1.2 },
        },
        {
          x, y: ratio,
          type: "scatter", mode: "lines",
          name: "Overcut ratio",
          line: { color: "#c8102e", width: 1.2, dash: "dash" },
          yaxis: "y2",
        },
      );
    }
    return traces;
  }, [series.data, pipeOdMm, overcutMm]);

  return (
    <PlotCard
      title="Overcut analysis"
      subtitle={
        pipeOdMm && overcutMm
          ? `Pipe OD ${pipeOdMm} mm · overcut ${overcutMm} mm`
          : "Set pipe OD and overcut in drive config to enable ratio"
      }
      data={data as never}
      height={420}
      uirevision={`overcut-${fromIso}`}
      layout={{
        margin: { l: 56, r: 64, t: 16, b: 36 },
        xaxis: { title: { text: "Chainage (m)" } },
        yaxis: { title: { text: "Volume (L)" }, rangemode: "tozero" },
        yaxis2: {
          title: { text: "Ratio" },
          overlaying: "y", side: "right", showgrid: false,
          rangemode: "tozero",
        },
        legend: {
          x: 0.02, y: 0.98, xanchor: "left", yanchor: "top",
          bgcolor: "rgba(255,255,255,0.85)",
          bordercolor: "rgba(0,0,0,0.08)", borderwidth: 1,
          font: { size: 10, color: "#374151" },
        },
        showlegend: true,
      }}
    />
  );
}
